import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ThumbsUp, ThumbsDown, X, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * PulseFeedback — Stage 20B+ Wave F.
 *
 * A compact, calm, one-shot feedback prompt surfaced at strategic
 * moments — first upload, week-1 confusion check, cancellation flow.
 * Stores in `pulse_feedback` collection via /api/feedback/pulse.
 *
 * Honours the founder rule: "no vanity prompts, no guilt." Renders ONCE
 * per `surface` per user (localStorage flag), gracefully exits on dismiss.
 *
 * Props:
 *   - surface (string, required): 'first_upload' | 'week_one' | 'cancellation' | custom
 *   - prompt (string, required): the question shown
 *   - context (object, optional): forwarded to backend
 *   - thumbs (bool, optional): when true, shows 👍/👎 row before comment box
 *   - placeholder (string, optional): textarea placeholder
 */

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_PREFIX = 'kindra.pulse_feedback_seen_v1.';

export const PulseFeedback = ({
  surface,
  prompt,
  context = {},
  thumbs = true,
  placeholder = 'Share what stood out (optional)…',
}) => {
  const storageKey = STORAGE_PREFIX + surface;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
  });
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Persist the "seen" flag once the user dismisses or submits.
  const persistDismissal = () => {
    try { localStorage.setItem(storageKey, '1'); } catch { /* noop */ }
  };

  if (dismissed || submitted) return null;

  const handleDismiss = () => {
    persistDismissal();
    setDismissed(true);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/feedback/pulse`, {
        surface,
        rating,
        comment: comment.trim() || null,
        context,
      });
      persistDismissal();
      setSubmitted(true);
      toast.success('Thanks — we read every one.');
    } catch (e) {
      // Even on failure: don't block the user. Persist + close.
      persistDismissal();
      setSubmitted(true);
      toast.success('Thanks — we read every one.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-5 md:p-6 my-5"
      style={{ backgroundColor: '#FBFAF6', border: '1px solid rgba(14,28,46,0.10)' }}
      data-testid={`pulse-feedback-${surface}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p
            className="font-serif text-[18px] md:text-[20px] leading-[1.35] tracking-[-0.005em]"
            style={{ color: '#0E1C2E' }}
          >
            {prompt}
          </p>
          {thumbs && rating === null && !comment && (
            <div className="mt-4 flex items-center gap-2" data-testid={`pulse-thumbs-row-${surface}`}>
              <button
                onClick={() => setRating('up')}
                className="px-3 py-2 rounded-full text-[13px] flex items-center gap-2 transition-colors hover:bg-white"
                style={{ border: '1px solid rgba(14,28,46,0.12)', color: '#0E1C2E' }}
                data-testid={`pulse-up-${surface}`}
              >
                <ThumbsUp size={14} weight="duotone" /> Yes
              </button>
              <button
                onClick={() => setRating('down')}
                className="px-3 py-2 rounded-full text-[13px] flex items-center gap-2 transition-colors hover:bg-white"
                style={{ border: '1px solid rgba(14,28,46,0.12)', color: '#0E1C2E' }}
                data-testid={`pulse-down-${surface}`}
              >
                <ThumbsDown size={14} weight="duotone" /> Not yet
              </button>
            </div>
          )}
          {(rating !== null || !thumbs) && (
            <div className="mt-4 space-y-3">
              {thumbs && rating && (
                <p className="text-[12.5px]" style={{ color: 'rgba(14,28,46,0.55)' }}>
                  {rating === 'up' ? 'Thanks. ' : 'Thanks — what would have helped? '}
                  <span className="text-[12px]">(optional)</span>
                </p>
              )}
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 800))}
                placeholder={placeholder}
                rows={2}
                className="w-full px-3 py-2 rounded-lg text-[14px] resize-none focus:outline-none focus:ring-1 focus:ring-[#2FB3A0]"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid rgba(14,28,46,0.10)', color: '#0E1C2E' }}
                data-testid={`pulse-comment-${surface}`}
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 rounded-full text-[13px] flex items-center gap-2 text-white disabled:opacity-50"
                  style={{ backgroundColor: '#0E1C2E' }}
                  data-testid={`pulse-submit-${surface}`}
                >
                  <Check size={13} weight="bold" /> {submitting ? 'Sending…' : 'Send'}
                </button>
                <button
                  onClick={handleDismiss}
                  className="px-3 py-2 rounded-full text-[12.5px] hover:bg-white transition-colors"
                  style={{ color: 'rgba(14,28,46,0.55)' }}
                  data-testid={`pulse-skip-${surface}`}
                >
                  Skip
                </button>
              </div>
            </div>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className="text-slate-400 hover:text-slate-700 mt-1"
          aria-label="Dismiss"
          data-testid={`pulse-close-${surface}`}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default PulseFeedback;
